import api from '../lib/api';
import type { ApiResponse } from '@maios/shared';

export interface OrgListItem {
  id: string;
  name: string;
  slug: string;
  planType: string;
  isActive: boolean;
  createdAt: string;
  planStartedAt: string | null;
  lastActivityAt: string | null;
  storageUsedBytes: number;
  storageLimitBytes: number;
  memberCount: number;
  projectCount: number;
  conversionDays: number | null;
}

export interface OrgListResponse {
  items: OrgListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface OrgListParams {
  page?: number;
  limit?: number;
  search?: string;
  planType?: string;
  isActive?: string;
  sortBy?: string;
  sortOrder?: string;
}

export interface OrgDetail {
  organization: {
    id: string;
    name: string;
    slug: string;
    planType: string;
    planStartedAt: string | null;
    isActive: boolean;
    deactivatedAt: string | null;
    lastActivityAt: string | null;
    createdAt: string;
    updatedAt: string;
  };
  owner: { id: string; email: string; name: string } | null;
  stats: {
    memberCount: number;
    projectCount: number;
    storageUsedBytes: number;
    storageLimitBytes: number;
  };
}

export interface OrgMember {
  id: string;
  userId: string;
  role: string;
  joinedAt: string;
  user: { id: string; name: string; email: string; role: string; createdAt: string; deletedAt: string | null };
  isDeactivated: boolean;
}

export interface OrgBug {
  id: string;
  title: string;
  category: string;
  status: string;
  reporterEmail: string;
  createdAt: string;
  user: { id: string; name: string; email: string } | null;
}

export async function getOrganizations(params: OrgListParams): Promise<OrgListResponse> {
  const query = new URLSearchParams();
  if (params.page) query.set('page', String(params.page));
  if (params.limit) query.set('limit', String(params.limit));
  if (params.search) query.set('search', params.search);
  if (params.planType) query.set('planType', params.planType);
  if (params.isActive) query.set('isActive', params.isActive);
  if (params.sortBy) query.set('sortBy', params.sortBy);
  if (params.sortOrder) query.set('sortOrder', params.sortOrder);

  const res = await api.get<ApiResponse<OrgListResponse>>(`/admin/organizations?${query}`);
  return res.data.data!;
}

export async function getOrganization(id: string): Promise<OrgDetail> {
  const res = await api.get<ApiResponse<OrgDetail>>(`/admin/organizations/${id}`);
  return res.data.data!;
}

export async function getOrganizationMembers(id: string): Promise<OrgMember[]> {
  const res = await api.get<ApiResponse<OrgMember[]>>(`/admin/organizations/${id}/members`);
  return res.data.data!;
}

export async function getOrganizationBugs(id: string): Promise<OrgBug[]> {
  const res = await api.get<ApiResponse<OrgBug[]>>(`/admin/organizations/${id}/bugs`);
  return res.data.data!;
}

// --- 운영 액션 ---

export async function updatePlan(id: string, planType: string) {
  const res = await api.patch<ApiResponse>(`/admin/organizations/${id}/plan`, { planType });
  return res.data.data as Record<string, unknown>;
}

export async function updateStorageLimit(id: string, storageLimitBytes: number) {
  const res = await api.patch<ApiResponse>(`/admin/organizations/${id}/storage-limit`, { storageLimitBytes });
  return res.data.data as Record<string, unknown>;
}

export async function updateStatus(id: string, isActive: boolean) {
  const res = await api.patch<ApiResponse>(`/admin/organizations/${id}/status`, { isActive });
  return res.data.data as Record<string, unknown>;
}
